import { Preferences } from "@capacitor/preferences";
import axios from "axios";

const PENDING_IDS_KEY = "externalPurchase:pendingIds";
const pendingKey = (id) => `externalPurchase:pending:${id}`;
const BACKOFF_STEPS_MS = [5000, 15000, 60000, 300000];
const MAX_PENDING_AGE_MS = 48 * 60 * 60 * 1000;
const PERMANENT_REJECTION_REASONS = ["already_reported", "product_mismatch"];

const isExpired = (record) =>
    Date.now() - new Date(record.createdAt).getTime() > MAX_PENDING_AGE_MS;

const getStorageItem = async (key) => {
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
};

const setStorageItem = (key, value) =>
    Preferences.set({ key, value: JSON.stringify(value) });

const getPendingIds = async () => (await getStorageItem(PENDING_IDS_KEY)) || [];

const setPendingIds = (ids) => setStorageItem(PENDING_IDS_KEY, ids);

const addPendingId = async (id) => {
    const ids = await getPendingIds();
    if (!ids.includes(id)) {
        await setPendingIds([...ids, id]);
    }
};

const removePendingId = async (id) => {
    const ids = await getPendingIds();
    await setPendingIds(ids.filter((existingId) => existingId !== id));
};

export const getPendingRecord = (uniqueIdentifier) =>
    getStorageItem(pendingKey(uniqueIdentifier));

export const createPendingRecord = async ({
    uniqueIdentifier,
    productId,
    externalPurchaseId,
}) => {
    const record = {
        uniqueIdentifier,
        productId,
        externalPurchaseId,
        createdAt: new Date().toISOString(),
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
    };

    await setStorageItem(pendingKey(uniqueIdentifier), record);
    await addPendingId(uniqueIdentifier);

    return record;
};

export const clearPendingRecord = async (uniqueIdentifier) => {
    await Preferences.remove({ key: pendingKey(uniqueIdentifier) });
    await removePendingId(uniqueIdentifier);
};

const isDueForRetry = (record) => {
    if (!record.lastAttemptAt) {
        return true;
    }

    const step = Math.min(
        Math.max(record.attempts - 1, 0),
        BACKOFF_STEPS_MS.length - 1
    );
    const waitMs = BACKOFF_STEPS_MS[step];

    return Date.now() - new Date(record.lastAttemptAt).getTime() >= waitMs;
};

export const isRecordDueForRetry = isDueForRetry;

export const submitPending = async (uniqueIdentifier) => {
    const record = await getPendingRecord(uniqueIdentifier);
    if (!record) {
        return;
    }

    try {
        await axios.post(route("paywall.app_store.receive_token"), {
            product_code: record.productId,
            unique_identifier: record.uniqueIdentifier,
            external_transaction_token: record.externalPurchaseId,
        });

        await clearPendingRecord(uniqueIdentifier);
    } catch (error) {
        if (
            PERMANENT_REJECTION_REASONS.includes(error?.response?.data?.reason)
        ) {
            await clearPendingRecord(uniqueIdentifier);
            return;
        }

        await setStorageItem(pendingKey(uniqueIdentifier), {
            ...record,
            attempts: record.attempts + 1,
            lastAttemptAt: new Date().toISOString(),
            lastError: error?.message || "unknown error",
        });
    }
};

export const findPendingRecords = async () => {
    const ids = await getPendingIds();
    const records = [];

    for (const id of ids) {
        const record = await getPendingRecord(id);
        if (record) {
            records.push(record);
        }
    }

    return records;
};

export const sweepPending = async () => {
    const ids = await getPendingIds();

    for (const id of ids) {
        const record = await getPendingRecord(id);
        if (!record) {
            continue;
        }

        if (isExpired(record)) {
            await clearPendingRecord(id);
            continue;
        }

        if (isDueForRetry(record)) {
            await submitPending(id);
        }
    }
};
